import React, { useEffect, useState } from "react";
import axios from "axios";
import { MdDelete } from "react-icons/md";
import { Icon } from "organisms/iconCommonent";
import { FaRegEdit } from "react-icons/fa";
import { AiOutlinePlusCircle } from "react-icons/ai";
import { Entrepreneur } from "./entrepreneurs";
import { ApiContent } from "./shaping";

const Goal: React.FC = React.memo(() => {
  const [entrepreneurs, setEntrepreneurs] = useState<Entrepreneur[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCard, setEditingCard] = useState<Entrepreneur | null>(null);
  const [apiContent, setApiContent] = useState<ApiContent | null>(null);
  const [showContentForm, setShowContentForm] = useState(false);

  // Fetch entrepreneurs and content
  const fetchEntrepreneurs = async () => {
    try {
      const [res, content] = await Promise.all([
        axios.get(`${process.env.REACT_APP_LOCAL_URL}/api/goal`),
         axios.get(`${process.env.REACT_APP_LOCAL_URL}/api/content/68efcc0e2d25c3fab3031989`),
       
      ]);

      if (content.data.status === "success") setApiContent(content.data.data);
      if (res.data.status === "success") setEntrepreneurs(res.data.message);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchEntrepreneurs();
  }, []);

  // Delete
  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`${process.env.REACT_APP_LOCAL_URL}/api/goal/${id}`);
      fetchEntrepreneurs();
    } catch (error) {
      console.error("Error deleting:", error);
    }
  };

  // Edit / Add
  const handleEdit = (item?: Entrepreneur) => {
    setEditingCard(item || null);
    setShowForm(true);
  };

  return (
    <section className="pt-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-lato-regular">Section - 06</h2>
        <hr />
      </div>

      <div className="w-full bg-white border border-gray-200 rounded-xl overflow-hidden p-4">
        <div className="md:flex items-center justify-between border-b border-[#84848480] border-opacity-50">
          <h4 className="pb-3 font-lato-bold text-[#07393C]">
            {apiContent?.heading}
          </h4>
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded"
            onClick={() => setShowContentForm(true)}
          >
            <Icon icon={FaRegEdit} />
          </button>
        </div>

        {/* Add New Card Button */}
        <div className="flex justify-end">
          <button
            className="bg-darkCyan mt-4 flex items-center text-white font-lato-regular rounded-md px-3 py-2"
            onClick={() => handleEdit()}
          >
            <span className="text-2xl pr-2">
              <Icon icon={AiOutlinePlusCircle} />
            </span>
            <h6>Add new card</h6>
          </button>
        </div>

        {/* Entrepreneur Cards */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {entrepreneurs.map((elem) => (
            <div
              key={elem._id}
              className="h-auto py-4 flex flex-col justify-between border-2 border-black/10 rounded-lg px-3"
            >
              {elem.image && (
                <img
                  src={`${process.env.REACT_APP_LOCAL_URL}/${elem.image}`}
                  alt={elem.title}
                  className="w-32 h-24 object-contain object-top rounded-md"
                />
              )}
              <h6 className="font-lato-bold text-lg mt-3">{elem.title}</h6>

              <div className="flex justify-start gap-3 mt-3">
                <button
                  onClick={() => handleEdit(elem)}
                  className="bg-blue-500 text-white px-2 py-1 rounded"
                >
                  <Icon icon={FaRegEdit} />
                </button>
                <button
                  onClick={() => handleDelete(elem._id!)}
                  className="text-red-500 border border-red-500 px-2 py-1 rounded"
                >
                  <Icon icon={MdDelete} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <EntrepreneurFormModal
          onClose={() => setShowForm(false)}
          fetchEntrepreneurs={fetchEntrepreneurs}
          editingCard={editingCard}
        />
      )}

      {/* Section Content Modal */}
      {showContentForm && apiContent && (
        <SectionContentModal
          onClose={() => setShowContentForm(false)}
          apiContent={apiContent}
          fetchEntrepreneurs={fetchEntrepreneurs}
        />
      )}
    </section>
  );
});

export default Goal;

//
// ==================== ENTREPRENEUR FORM MODAL ====================
//
interface EntrepreneurFormModalProps {
  onClose: () => void;
  fetchEntrepreneurs: () => void;
  editingCard: Entrepreneur | null;
}

const EntrepreneurFormModal = ({
  onClose,
  fetchEntrepreneurs,
  editingCard,
}: EntrepreneurFormModalProps) => {
  const [title, setTitle] = useState(editingCard?.title || "");
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(
    editingCard?.image ? `${process.env.REACT_APP_LOCAL_URL}/${editingCard.image}` : null
  );

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
      setPreview(URL.createObjectURL(file)); // ✅ preview new image
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const formData = new FormData();
      formData.append("title", title);
      if (image) formData.append("image", image); // ✅ only append if selected

      const url = editingCard
        ? `${process.env.REACT_APP_LOCAL_URL}/api/goal/${editingCard._id}`
        : `${process.env.REACT_APP_LOCAL_URL}/api/goal`;

      const method = editingCard ? "put" : "post";

      await axios({
        method,
        url,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      fetchEntrepreneurs();
      onClose();
    } catch (error) {
      console.error(" Error submitting form:", error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-xl w-[500px] shadow-lg">
        <h3 className="text-lg font-lato-bold mb-4">
          {editingCard ? "Edit Card" : "Add New Card"}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <label className="block font-lato-bold">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2"
              required
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block font-lato-bold">Image</label>
            {preview && (
              <img
                src={preview}
                alt="preview"
                className="w-32 h-24 object-contain rounded-md mb-2"
              />
            )}
            <input
              type="file"
              onChange={handleImageChange}
              className="w-full border border-gray-300 rounded-md p-2"
              accept="image/*"
            />
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {editingCard ? "Update" : "Add"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-red-500 border border-red-500 px-4 py-2 rounded-md hover:bg-red-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

//
// ==================== SECTION CONTENT MODAL (UNCHANGED) ====================
//
interface SectionContentModalProps {
  onClose: () => void;
  apiContent: ApiContent;
  fetchEntrepreneurs: () => void;
}

const SectionContentModal = ({
  onClose,
  apiContent,
  fetchEntrepreneurs,
}: SectionContentModalProps) => {
  const [heading, setHeading] = useState(apiContent.heading);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.patch(
        `${process.env.REACT_APP_LOCAL_URL}/api/content/68ecdee41626ae80372820a4`,
        { heading }
      );
      fetchEntrepreneurs();
      onClose();
    } catch (error) {
      console.error("Error updating section:", error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-xl w-[500px] shadow-lg">
        <h3 className="text-lg font-lato-bold mb-4">Edit Section Content</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block font-lato-bold">Heading</label>
            <input
              type="text"
              value={heading}
              onChange={(e) => setHeading(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2"
              required
            />
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <button
              type="submit"
              className="px-4 py-2 bg-teal text-white rounded-md"
            >
              Update
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-red-500 border border-red-500 px-4 py-2 rounded-md hover:bg-red-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
